import styled from '@emotion/styled';
import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { withTheme } from '@mui/styles';

import { useAuth } from '@selfserve/react-hooks';
import { buttonIds } from '@selfserve/react-utils';

import {
  StyledLogo,
  StyledMenuButton,
  StyledMenu,
  StyledMenuItem,
} from './styled-components';

const StyledHeaderContainer = styled.div(({ theme }: { theme?: any }) => ({
  width: '100%',
  height: '60px',
  display: 'flex',
  paddingTop: theme.spacing(1),
  paddingBottom: 0,
  paddingLeft: theme.spacing(2),
  paddingRight: '28px',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledButtonsGroup = styled('div')(({ theme }: { theme?: any }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.up('sm')]: {
    '> :first-of-type': {
      marginRight: '22px',
    },
  },
}));

interface Props {
  displayName?: string;
  theme: any;
}

const Header = withTheme(({ displayName }: Props) => {
  const { t } = useTranslation();
  const dropdownMenuRef = useRef<HTMLDivElement>();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { logout } = useAuth();

  const handleToggle = useCallback(
    (callback: Dispatch<SetStateAction<boolean>>) => () =>
      callback((value) => !value),
    []
  );

  const handleLogout = useCallback(
    () => logout(handleToggle(setIsDropdownOpen)),
    [handleToggle, logout]
  );

  return (
    <StyledHeaderContainer>
      <StyledLogo />
      {displayName && (
        <StyledButtonsGroup>
          <div ref={dropdownMenuRef as MutableRefObject<HTMLDivElement>}>
            <StyledMenuButton
              onClick={handleToggle(setIsDropdownOpen)}
              id={buttonIds.accountName}
            >
              {displayName}
            </StyledMenuButton>
            <StyledMenu
              open={isDropdownOpen}
              anchorEl={dropdownMenuRef.current}
              onClose={handleToggle(setIsDropdownOpen)}
            >
              <StyledMenuItem
                onClick={handleLogout}
                id={buttonIds.overviewLogout}
              >
                {t('common.logout')}
              </StyledMenuItem>
            </StyledMenu>
          </div>
        </StyledButtonsGroup>
      )}
    </StyledHeaderContainer>
  );
});

export { Header };
